import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { Users, ChevronLeft, ChevronRight, Mail, ShieldCheck, ShieldX, Ban, CheckCircle2, Trash2, RefreshCw, UserPlus, Search, History } from 'lucide-react';

const PER_PAGE = 50;

const ACTION_LABELS = {
  create: 'Creó la cuenta',
  update_email: 'Cambió el email',
  confirm_email: 'Confirmó el email',
  unconfirm_email: 'Desconfirmó el email',
  ban: 'Baneó al usuario',
  unban: 'Quitó el baneo',
  delete: 'Eliminó la cuenta',
  resend_confirmation: 'Reenvió el email de confirmación',
  password_recovery: 'Envió recuperación de contraseña',
};

const FILTERS = [
  { key: 'all', label: 'Todos' },
  { key: 'confirmed', label: 'Confirmados' },
  { key: 'unconfirmed', label: 'Sin confirmar' },
  { key: 'banned', label: 'Baneados' },
];

function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }); } catch { return s; }
}

const isBanned = (u) => u.banned_until && new Date(u.banned_until) > new Date();
const isConfirmed = (u) => !!u.email_confirmed_at;

export default function SupabaseAuthUsers() {
  const [allUsers, setAllUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [emailEdit, setEmailEdit] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', password: '', email_confirm: true });
  const [history, setHistory] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/supabase/auth/all-users');
      setAllUsers(data.users || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo cargar la lista de usuarios');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [query, status]);

  const counts = useMemo(() => ({
    all: allUsers.length,
    confirmed: allUsers.filter(isConfirmed).length,
    unconfirmed: allUsers.filter((u) => !isConfirmed(u)).length,
    banned: allUsers.filter(isBanned).length,
  }), [allUsers]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allUsers.filter((u) => {
      if (status === 'confirmed' && !isConfirmed(u)) return false;
      if (status === 'unconfirmed' && isConfirmed(u)) return false;
      if (status === 'banned' && !isBanned(u)) return false;
      if (q && !((u.email || '').toLowerCase().includes(q) || (u.id || '').toLowerCase().includes(q))) return false;
      return true;
    });
  }, [allUsers, query, status]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageUsers = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const runAction = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(successMsg);
      setEmailEdit(null); setCreateOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error en la operación');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const askConfirm = (u, action) => {
    const cfg = {
      confirm: {
        title: '¿Confirmar el email de este usuario?',
        description: `Se marcará el email de "${u.email}" como confirmado. El usuario podrá iniciar sesión.`,
        danger: false,
        run: () => api.put(`/supabase/auth/users/${u.id}/email-confirmation`, { confirmed: true }),
        msg: 'Email confirmado',
      },
      unconfirm: {
        title: '¿Desconfirmar el email?',
        description: `El email de "${u.email}" quedará sin confirmar.`,
        danger: false,
        run: () => api.put(`/supabase/auth/users/${u.id}/email-confirmation`, { confirmed: false }),
        msg: 'Email desconfirmado',
      },
      ban: {
        title: '¿Banear a este usuario?',
        description: `"${u.email}" no podrá iniciar sesión hasta que se le quite el baneo.`,
        danger: true,
        run: () => api.put(`/supabase/auth/users/${u.id}/ban`, { banned: true }),
        msg: 'Usuario baneado',
      },
      unban: {
        title: '¿Quitar el baneo?',
        description: `"${u.email}" podrá volver a iniciar sesión.`,
        danger: false,
        run: () => api.put(`/supabase/auth/users/${u.id}/ban`, { banned: false }),
        msg: 'Baneo retirado',
      },
      delete: {
        title: '¿Eliminar este usuario?',
        description: `Se eliminará permanentemente la cuenta "${u.email}" de la base de datos de Tu Iglesia. Esta acción no se puede deshacer.`,
        danger: true,
        run: () => api.delete(`/supabase/auth/users/${u.id}`),
        msg: 'Usuario eliminado',
      },
    }[action];
    setConfirm({ ...cfg });
  };

  const submitEmail = () => {
    const u = emailEdit.user; const value = emailEdit.value?.trim();
    if (!value || value === u.email) { toast.info('Ingresa un email distinto'); return; }
    setConfirm({
      title: '¿Cambiar el email?',
      description: `El email de "${u.email}" se cambiará a "${value}" directamente en producción.`,
      danger: false,
      run: () => api.put(`/supabase/auth/users/${u.id}/email`, { email: value }),
      msg: 'Email actualizado',
    });
  };

  const submitCreate = () => {
    const email = createForm.email?.trim();
    const password = createForm.password;
    if (!email || !password) { toast.info('Email y contraseña son obligatorios'); return; }
    if (password.length < 6) { toast.info('La contraseña debe tener al menos 6 caracteres'); return; }
    setConfirm({
      title: '¿Crear este usuario?',
      description: `Se creará la cuenta "${email}" en la base de datos de producción de Tu Iglesia${createForm.email_confirm ? ' (email ya confirmado)' : ' (email sin confirmar)'}.`,
      danger: false,
      run: () => api.post('/supabase/auth/users', { email, password, email_confirm: createForm.email_confirm }),
      msg: 'Usuario creado',
    });
  };

  const openHistory = async (u) => {
    setHistory({ user: u, items: [], loading: true });
    try {
      const { data } = await api.get('/supabase/auth/audit', { params: { uid: u.id, limit: 100 } });
      setHistory({ user: u, items: data || [], loading: false });
    } catch (e) {
      setHistory({ user: u, items: [], loading: false });
      toast.error('No se pudo cargar el historial');
    }
  };

  return (
    <div className="space-y-4" data-testid="supabase-auth-users">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))] font-mono">
          <Users className="h-4 w-4 text-[hsl(var(--primary))]" />
          <span>Usuarios de Supabase Auth (producción)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[hsl(var(--muted-foreground))]" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar en todos…" className="pl-8 h-9 w-56 text-xs" data-testid="auth-search-input" />
          </div>
          <Button size="sm" onClick={() => { setCreateForm({ email: '', password: '', email_confirm: true }); setCreateOpen(true); }} data-testid="auth-create-btn">
            <UserPlus className="h-3.5 w-3.5 mr-1" /> Nuevo usuario
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="auth-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="auth-status-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setStatus(f.key)}
            data-testid={`auth-filter-${f.key}`}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors duration-150 ${status === f.key ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary)/.15)] text-[hsl(var(--primary))]' : 'border-border bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:border-[hsl(var(--primary)/.4)]'}`}
          >
            {f.label} <span className="ml-1 font-mono opacity-70">{counts[f.key]}</span>
          </button>
        ))}
      </div>

      {error ? (
        <Card className="p-6 bg-card border border-red-500/30 text-red-300" data-testid="auth-users-error">
          <div className="font-semibold mb-1">No disponible</div>
          <div className="text-xs font-mono">{String(error)}</div>
        </Card>
      ) : (
        <Card className="bg-card border border-border">
          <div className="max-h-[520px] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-card z-10">
                <tr className="text-left text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] border-b border-border">
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Estado</th>
                  <th className="px-3 py-2">Creado</th>
                  <th className="px-3 py-2">Último acceso</th>
                  <th className="px-3 py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {pageUsers.map((u) => {
                  const confirmed = isConfirmed(u);
                  const banned = isBanned(u);
                  return (
                    <tr key={u.id} className="border-b border-border/40 hover:bg-accent/40" data-testid={`auth-user-row-${u.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium">{u.email || <span className="text-[hsl(var(--muted-foreground))]">sin email</span>}</div>
                        <div className="text-[10px] font-mono text-[hsl(var(--muted-foreground))]">{u.id}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          <Badge variant={confirmed ? 'default' : 'secondary'} className={confirmed ? 'bg-emerald-600/80' : ''}>{confirmed ? 'Confirmado' : 'Sin confirmar'}</Badge>
                          {banned && <Badge variant="destructive">Baneado</Badge>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs font-mono text-[hsl(var(--muted-foreground))]">{fmtDate(u.created_at)}</td>
                      <td className="px-3 py-2 text-xs font-mono text-[hsl(var(--muted-foreground))]">{fmtDate(u.last_sign_in_at)}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" title="Historial" onClick={() => openHistory(u)} data-testid={`auth-history-${u.id}`}><History className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="sm" title="Editar email" onClick={() => setEmailEdit({ user: u, value: u.email || '' })} data-testid={`auth-edit-email-${u.id}`}><Mail className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="sm" title={confirmed ? 'Desconfirmar' : 'Confirmar'} onClick={() => askConfirm(u, confirmed ? 'unconfirm' : 'confirm')} data-testid={`auth-toggle-confirm-${u.id}`}>
                            {confirmed ? <ShieldX className="h-3.5 w-3.5 text-amber-400" /> : <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />}
                          </Button>
                          <Button variant="ghost" size="sm" title={banned ? 'Desbanear' : 'Banear'} onClick={() => askConfirm(u, banned ? 'unban' : 'ban')} data-testid={`auth-toggle-ban-${u.id}`}>
                            {banned ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> : <Ban className="h-3.5 w-3.5 text-red-400" />}
                          </Button>
                          <Button variant="ghost" size="sm" title="Eliminar" onClick={() => askConfirm(u, 'delete')} data-testid={`auth-delete-${u.id}`}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && !loading && (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-[hsl(var(--muted-foreground))]">{query || status !== 'all' ? 'Ningún usuario coincide con el filtro o la búsqueda.' : 'No hay usuarios en Supabase Auth.'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="p-3 flex items-center justify-between border-t border-border">
            <span className="text-xs text-[hsl(var(--muted-foreground))]">{filtered.length} resultado(s) de {allUsers.length} usuario(s)</span>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="auth-prev-page"><ChevronLeft className="h-4 w-4" /></Button>
              <span className="text-xs font-mono">pág. {page} / {totalPages}</span>
              <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="auth-next-page"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
        </Card>
      )}

      {/* Create user dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="auth-create-dialog">
          <DialogHeader>
            <DialogTitle>Nuevo usuario de la app</DialogTitle>
            <DialogDescription>Crea una cuenta nueva en Supabase Auth (producción).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs">Email</Label>
              <Input type="email" value={createForm.email} onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))} data-testid="auth-create-email" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Contraseña temporal</Label>
              <Input type="text" value={createForm.password} onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))} placeholder="mín. 6 caracteres" data-testid="auth-create-password" />
            </div>
            <div className="flex items-center justify-between pt-1">
              <Label className="text-xs">Marcar email como confirmado</Label>
              <Switch checked={createForm.email_confirm} onCheckedChange={(v) => setCreateForm((f) => ({ ...f, email_confirm: v }))} data-testid="auth-create-confirm-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={submitCreate} data-testid="auth-create-save">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit email dialog */}
      <Dialog open={!!emailEdit} onOpenChange={(v) => !v && setEmailEdit(null)}>
        <DialogContent data-testid="auth-email-dialog">
          <DialogHeader>
            <DialogTitle>Editar email</DialogTitle>
            <DialogDescription>Cambia la dirección de correo de este usuario.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-xs">Nuevo email</Label>
            <Input type="email" value={emailEdit?.value ?? ''} onChange={(e) => setEmailEdit((s) => ({ ...s, value: e.target.value }))} data-testid="auth-email-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEmailEdit(null)}>Cancelar</Button>
            <Button onClick={submitEmail} data-testid="auth-email-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!history} onOpenChange={(v) => !v && setHistory(null)}>
        <DialogContent className="max-w-lg" data-testid="auth-history-dialog">
          <DialogHeader>
            <DialogTitle>Historial de cambios</DialogTitle>
            <DialogDescription>{history?.user?.email} — acciones registradas en el BOMM</DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto space-y-2 py-1">
            {history?.loading && <div className="text-xs text-[hsl(var(--muted-foreground))]">Cargando…</div>}
            {!history?.loading && (history?.items?.length || 0) === 0 && (
              <div className="text-sm text-[hsl(var(--muted-foreground))] text-center py-6">Sin cambios registrados para este usuario.</div>
            )}
            {(history?.items || []).map((it, i) => (
              <div key={i} className="flex items-start justify-between gap-3 p-2.5 rounded-lg border border-border bg-[hsl(var(--muted))]" data-testid={`auth-history-item-${i}`}>
                <div>
                  <div className="text-sm font-medium text-foreground">{ACTION_LABELS[it.action] || it.action}</div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))]">por {it.actor_name || it.actor_email || 'desconocido'}{it.metadata?.email ? ` → ${it.metadata.email}` : ''}</div>
                </div>
                <div className="text-[10px] font-mono text-[hsl(var(--muted-foreground))] whitespace-nowrap">{fmtDate(it.created_at)}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation (always) */}
      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent data-testid="auth-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-foreground border-border" data-testid="auth-confirm-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={(e) => { e.preventDefault(); runAction(confirm.run, confirm.msg || 'Hecho'); }}
              className={confirm?.danger ? 'bg-red-600 hover:bg-red-700' : ''}
              data-testid="auth-confirm-accept"
            >
              {busy ? 'Procesando…' : 'Confirmar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
